import "core-js/modules/es.array.filter.js";
import "core-js/modules/es.object.get-own-property-descriptors.js";
import "core-js/modules/web.dom-collections.for-each.js";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { ProtocolVersion, Messages, formatMessage } from '../constants';

/**
 * Validate protocol version
 * 
 * Only checks minimum supported version, not maximum version
 * Because new versions usually maintain backward compatibility with older message formats
 * 
 * @param version protocol version number
 * @returns validation result
 */
export function validateProtocolVersion(version) {
  if (typeof version !== 'number' || !Number.isInteger(version)) {
    return {
      valid: false,
      error: Messages.INVALID_PROTOCOL_VERSION_FORMAT,
      errorCode: 'INVALID_FORMAT'
    };
  }
  if (version < ProtocolVersion.MIN_SUPPORTED) {
    return {
      valid: false,
      version,
      error: formatMessage(Messages.PROTOCOL_VERSION_TOO_LOW, version, ProtocolVersion.MIN_SUPPORTED),
      errorCode: 'VERSION_TOO_LOW'
    };
  }

  // Don't check maximum version, new versions usually maintain backward compatibility
  return {
    valid: true,
    version
  };
}

/**
 * Validate PostMessage data format (full validation)
 * @param data data to validate
 * @returns validation result, including protocol version info
 */
export function validatePostMessage(data) {
  // Basic format validation
  if (!data || typeof data !== 'object') {
    return {
      valid: false,
      error: Messages.INVALID_MESSAGE_FORMAT_NOT_OBJECT,
      errorCode: 'INVALID_FORMAT'
    };
  }

  // Protocol identifier validation
  if (data.__requestIframe__ === undefined) {
    return {
      valid: false,
      error: Messages.INVALID_MESSAGE_FORMAT_MISSING_PROTOCOL,
      errorCode: 'INVALID_FORMAT'
    };
  }

  // Protocol version validation
  var versionResult = validateProtocolVersion(data.__requestIframe__);
  if (!versionResult.valid) {
    return versionResult;
  }

  // Required field validation
  if (typeof data.type !== 'string') {
    return {
      valid: false,
      version: versionResult.version,
      error: Messages.INVALID_MESSAGE_FORMAT_MISSING_TYPE,
      errorCode: 'INVALID_FORMAT'
    };
  }
  if (typeof data.requestId !== 'string') {
    return {
      valid: false,
      version: versionResult.version,
      error: Messages.INVALID_MESSAGE_FORMAT_MISSING_REQUEST_ID,
      errorCode: 'INVALID_FORMAT'
    };
  }
  return {
    valid: true,
    version: versionResult.version,
    data: data
  };
}

/**
 * Check if data is a request-iframe framework message (only checks basic format, doesn't validate version compatibility)
 * @param data data to check
 * @returns whether it's a request-iframe message
 */
export function isRequestIframeMessage(data) {
  return !!(data && typeof data === 'object' && typeof data.__requestIframe__ === 'number' && typeof data.type === 'string' && typeof data.requestId === 'string');
}

/**
 * Create PostMessage data
 * @param type message type
 * @param requestId request ID
 * @param data additional data
 * @returns PostMessageData
 */
export function createPostMessage(type, requestId, data) {
  return _objectSpread({
    __requestIframe__: ProtocolVersion.CURRENT,
    timestamp: Date.now(),
    type,
    requestId
  }, data);
}

/**
 * Validate PostMessage data format (only checks basic format, doesn't validate version compatibility)
 * Used for quick determination of whether it's a request-iframe framework message
 * 
 * Note: This method doesn't check protocol version compatibility, use isCompatibleVersion for version compatibility check
 * 
 * @param data data to validate
 * @returns whether it's a valid PostMessageData
 */
export function isValidPostMessage(data) {
  return !!(data && typeof data === 'object' && typeof data.__requestIframe__ === 'number' && typeof data.type === 'string' && typeof data.requestId === 'string');
}

/**
 * Get protocol version from message
 * @param data PostMessageData
 * @returns protocol version number, undefined if invalid
 */
export function getProtocolVersion(data) {
  if (data && typeof data === 'object' && typeof data.__requestIframe__ === 'number') {
    return data.__requestIframe__;
  }
  return undefined;
}

/**
 * Check if protocol version is compatible
 * 
 * Only checks minimum supported version, not maximum version
 * Because new versions usually maintain backward compatibility with older message formats
 * 
 * @param version protocol version number
 * @returns whether compatible
 */
export function isCompatibleVersion(version) {
  return version >= ProtocolVersion.MIN_SUPPORTED;
}